import React, { useState, useEffect } from 'react';
import { getSupabase } from './supabaseClient';
import { Box, Button, Input, Select, FormControl, FormLabel, FormHelperText, useToast, Heading, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import { TwilioHealthStatus } from './settings';
import { isInGroup, normalizeRole, hasClinicalAdminPermissions } from '../config/roles';
import LLMUsageTab from './LLMUsageTab';
import DbHealthPanel from '../pages/superadmin/liveActivity/DbHealthPanel';
import { runDbHealthCheck } from '../services/liveActivity';

// The DB Health tab is for the roles its backend check admits (admin, doctor,
// superadmin). The role is read from Supabase, the very thing the tab diagnoses,
// so whether this browser's user had the tab is remembered: when Supabase is
// down and the role can't load, they still get it. Showing the tab grants
// nothing; the backend decides what the check returns.
const DB_HEALTH_TAB_KEY = 'settings:db-health-tab';

const hadDbHealthTab = () => {
  try {
    return window.localStorage.getItem(DB_HEALTH_TAB_KEY) === '1';
  } catch {
    return false;
  }
};

const rememberDbHealthTab = (allowed) => {
  try {
    if (allowed) window.localStorage.setItem(DB_HEALTH_TAB_KEY, '1');
    else window.localStorage.removeItem(DB_HEALTH_TAB_KEY);
  } catch {
    /* storage blocked: the tab just won't be remembered */
  }
};

// Curated IANA timezone list grouped by region
const TIMEZONES = [
  // United States
  { value: 'America/New_York',    label: 'Eastern Time (ET) — New York' },
  { value: 'America/Chicago',     label: 'Central Time (CT) — Chicago' },
  { value: 'America/Denver',      label: 'Mountain Time (MT) — Denver' },
  { value: 'America/Phoenix',     label: 'Mountain Time no DST — Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) — Los Angeles' },
  { value: 'America/Anchorage',   label: 'Alaska Time — Anchorage' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii Time — Honolulu' },
  // Canada
  { value: 'America/Toronto',     label: 'Eastern Time — Toronto' },
  { value: 'America/Vancouver',   label: 'Pacific Time — Vancouver' },
  { value: 'America/Edmonton',    label: 'Mountain Time — Edmonton' },
  { value: 'America/Winnipeg',    label: 'Central Time — Winnipeg' },
  { value: 'America/Halifax',     label: 'Atlantic Time — Halifax' },
  // Europe
  { value: 'Europe/London',       label: 'GMT/BST — London' },
  { value: 'Europe/Paris',        label: 'CET/CEST — Paris' },
  { value: 'Europe/Berlin',       label: 'CET/CEST — Berlin' },
  { value: 'Europe/Madrid',       label: 'CET/CEST — Madrid' },
  { value: 'Europe/Rome',         label: 'CET/CEST — Rome' },
  { value: 'Europe/Athens',       label: 'EET/EEST — Athens' },
  { value: 'Europe/Moscow',       label: 'MSK — Moscow' },
  // Asia / Pacific
  { value: 'Asia/Dubai',          label: 'GST — Dubai' },
  { value: 'Asia/Karachi',        label: 'PKT — Karachi' },
  { value: 'Asia/Kolkata',        label: 'IST — Kolkata' },
  { value: 'Asia/Dhaka',          label: 'BST — Dhaka' },
  { value: 'Asia/Bangkok',        label: 'ICT — Bangkok' },
  { value: 'Asia/Singapore',      label: 'SGT — Singapore' },
  { value: 'Asia/Shanghai',       label: 'CST — Shanghai' },
  { value: 'Asia/Tokyo',          label: 'JST — Tokyo' },
  { value: 'Asia/Seoul',          label: 'KST — Seoul' },
  { value: 'Australia/Sydney',    label: 'AEDT/AEST — Sydney' },
  { value: 'Pacific/Auckland',    label: 'NZDT/NZST — Auckland' },
  // Latin America
  { value: 'America/Sao_Paulo',   label: 'BRT — São Paulo' },
  { value: 'America/Mexico_City', label: 'CST/CDT — Mexico City' },
  { value: 'America/Bogota',      label: 'COT — Bogotá' },
  { value: 'America/Lima',        label: 'PET — Lima' },
  { value: 'America/Santiago',    label: 'CLT/CLST — Santiago' },
  { value: 'America/Buenos_Aires', label: 'ART — Buenos Aires' },
  // Africa / Middle East
  { value: 'Africa/Cairo',        label: 'EET — Cairo' },
  { value: 'Africa/Johannesburg', label: 'SAST — Johannesburg' },
  { value: 'Africa/Lagos',        label: 'WAT — Lagos' },
];

const SettingsPage = () => {
  const [newUsername, setNewUsername] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [role, setRole] = useState('user');
  const [adminCode, setAdminCode] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [savedRole, setSavedRole] = useState(null);
  const [savedTimezone, setSavedTimezone] = useState(null);
  const toast = useToast();

  // Fetch user role on component mount
  useEffect(() => {
    const fetchUserRole = async () => {
      const supabase = await getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data, error } = await supabase
          .from('users')
          .select('role, timezone')
          .eq('id', session.user.id)
          .single();
        if (data && !error) {
          setUserRole(data.role);
          setRoleLoaded(true);
          rememberDbHealthTab(hasClinicalAdminPermissions(data.role));
          if (data.role) { setRole(data.role); setSavedRole(data.role); }
          if (data.timezone) { setTimezone(data.timezone); setSavedTimezone(data.timezone); }
        }
      }
    };
    fetchUserRole();
  }, []);

  const handleUpdateProfile = async () => {
    const supabase = await getSupabase();
    setIsSubmitting(true);

    const updatedFields = [];

    // Username
    if (newUsername) {
      const { error } = await supabase.auth.updateUser({ data: { username: newUsername } });
      if (error) {
        toast({ title: 'Error updating username', description: error.message, status: 'error', duration: 3000, isClosable: true });
      } else {
        updatedFields.push('username');
      }
    }

    // Email
    if (newEmail) {
      const { error } = await supabase.auth.updateUser({ email: newEmail });
      if (error) {
        toast({ title: 'Error updating email', description: error.message, status: 'error', duration: 3000, isClosable: true });
      } else {
        updatedFields.push('email');
      }
    }

    // Password
    if (currentPassword && newPassword) {
      const { error } = await supabase.auth.updateUser({ password: newPassword, data: { currentPassword } });
      if (error) {
        toast({ title: 'Error updating password', description: error.message, status: 'error', duration: 3000, isClosable: true });
      } else {
        updatedFields.push('password');
      }
    }

    const { data: { user } } = await supabase.auth.getUser();

    // Role — only save if changed
    if (role !== savedRole) {
      if (normalizeRole(role) === 'admin' && adminCode !== 'ADMIN1234') {
        toast({ title: 'Invalid admin code', description: 'Enter the correct admin code to gain admin privileges.', status: 'error', duration: 3000, isClosable: true });
      } else {
        const { error } = await supabase.from('users').update({ role }).eq('id', user.id);
        if (error) {
          toast({ title: 'Error updating role', description: error.message, status: 'error', duration: 3000, isClosable: true });
        } else {
          updatedFields.push('role');
          setUserRole(role);
          setSavedRole(role);
        }
      }
    }

    // Timezone — only save if changed. Write it to the user's row in EVERY schema
    // (public + tenants) via the RPC so all views agree no matter which schema they
    // read viewer tz from. Falls back to a single-schema update if the RPC isn't
    // deployed yet (sql/migrations/sync_user_timezone_across_schemas.sql).
    if (timezone !== savedTimezone) {
      let error = null;
      // The RPC lives in `public`, but the default client is pinned to the user's
      // ACTIVE (tenant) schema — so plain supabase.rpc() looks for
      // <activeSchema>.set_my_timezone_all_schemas and 404s. Target public
      // explicitly; the same authenticated client is used, so auth.uid() inside
      // the SECURITY DEFINER function still resolves to this user.
      const { error: rpcError } = await supabase
        .schema('public')
        .rpc('set_my_timezone_all_schemas', { p_timezone: timezone });
      if (rpcError) {
        console.warn(
          '[SettingsPage] set_my_timezone_all_schemas RPC unavailable, falling back to single-schema update:',
          rpcError.message || rpcError,
        );
        ({ error } = await supabase.from('users').update({ timezone }).eq('id', user.id));
      }
      if (error) {
        toast({ title: 'Error updating timezone', description: error.message, status: 'error', duration: 3000, isClosable: true });
      } else {
        updatedFields.push('timezone');
        setSavedTimezone(timezone);
      }
    }

    if (updatedFields.length > 0) {
      const fieldLabels = {
        username: 'Username',
        email: 'Email',
        password: 'Password',
        role: 'Role',
        timezone: 'Timezone',
      };
      const names = updatedFields.map((f) => fieldLabels[f] || f).join(', ');
      toast({
        title: `${names} updated`,
        description: `${names} ${updatedFields.length === 1 ? 'has' : 'have'} been updated successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }

    setIsSubmitting(false);
  };

  const showDbHealth = roleLoaded ? hasClinicalAdminPermissions(userRole) : hadDbHealthTab();

  return (
    <Box maxW="1200px" mx="auto" mt="20px" p="20px">
      <Heading mb={7}>Settings</Heading>
      
      {/* A tab's content loads the first time it's opened, then stays mounted. */}
      <Tabs variant="enclosed" isLazy lazyBehavior="keepMounted">
        <TabList>
          <Tab>Profile Settings</Tab>
          {!isInGroup(userRole, 'freemium') && <Tab>Twilio Health Status</Tab>}
          <Tab>LLM Usage</Tab>
          {showDbHealth && <Tab>DB Health</Tab>}
        </TabList>

        <TabPanels>
          <TabPanel>
            <Box maxW="500px">
              <FormControl mb="20px">
                <FormLabel>New Username</FormLabel>
                <Input
                  placeholder="Enter new username"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                />
              </FormControl>

              <FormControl mb="20px">
                <FormLabel>New Email</FormLabel>
                <Input
                  type="email"
                  placeholder="Enter new email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </FormControl>

              <FormControl mb="20px">
                <FormLabel>Current Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Enter current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </FormControl>

              <FormControl mb="20px">
                <FormLabel>New Password</FormLabel>
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </FormControl>

              <FormControl mb="20px">
                <FormLabel>Role</FormLabel>
                <Select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  {userRole && userRole !== 'user' && userRole !== 'admin' && (
                    <option value={userRole}>{userRole.charAt(0).toUpperCase() + userRole.slice(1)}</option>
                  )}
                </Select>
              </FormControl>

              {normalizeRole(role) === 'admin' && (
                <FormControl mb="20px">
                  <FormLabel>Admin Code</FormLabel>
                  <Input
                    type="password"
                    placeholder="Enter admin code"
                    value={adminCode}
                    onChange={(e) => setAdminCode(e.target.value)}
                  />
                </FormControl>
              )}

              <FormControl mb="20px">
                <FormLabel>Timezone</FormLabel>
                <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </Select>
                <FormHelperText>
                  Used for appointment versioning and other time-based features.
                </FormHelperText>
              </FormControl>

              <Button
                colorScheme="blue"
                onClick={handleUpdateProfile}
                isLoading={isSubmitting}
              >
                Update Profile
              </Button>
            </Box>
          </TabPanel>

          {!isInGroup(userRole, 'freemium') && (
            <TabPanel>
              <TwilioHealthStatus />
            </TabPanel>
          )}

          <TabPanel>
            <LLMUsageTab />
          </TabPanel>

          {showDbHealth && (
            <TabPanel>
              <DbHealthPanel run={runDbHealthCheck} />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default SettingsPage;
